// src/routes/index.tsx
import { createBrowserRouter, Navigate } from "react-router-dom";
import App from "../App";
import ReportLayout from "../components/AddressManagement/ReportLayout";
import Dashboard from "../components/Dashboard";
import CustomerDetails from "../components/Customer/CustomerDetails";
import ApiKeyDetails from "../components/Customer/ApiKeyDetails";
import Faq from "../components/Faq";
import Customers from "components/Customer/Customers";
import AddressValidateFeedback from "components/realtime/AddressValidateFeedback";
import GeocodeFeedback from "components/realtime/GeocodeFeedback";
import Debugging from '../components/Debugging';
import Login from '../Login';
import LoginCallback from '../LoginCallback';
import NotAuthorized from '../NotAuthorized';
import Pods from '../components/Pods';
import AddressManagement from '../components/AddressManagement/AddressManagement';
import AddressAliases from '../components/AddressManagement/AddressAliases';
import AddressDetails from '../components/AddressManagement/AddressDetails';
import ProviderDisagreementsPage from '../components/Reports/ProviderDisagreementsPage';
import ReportPage from '../components/AddressManagement/ReportPage';
import ReportPageManagement from "components/AddressManagement/ReportPageManagement";
import { useAuth } from "../AuthContext";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, hasAddressRole } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAddressRole) {
    return <Navigate to="/not-authorized" replace />;
  }

  return <>{children}</>;
};

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      {
        path: "/",
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: "/login",
        element: <Login />,
      },
      {
        path: "/login/callback",
        element: <LoginCallback />,
      },
      {
        path: "/not-authorized",
        element: <NotAuthorized />,
      },
      {
        path: "/dashboard",
        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: "/customers",
        element: (
          <ProtectedRoute>
            <Customers />
          </ProtectedRoute>
        ),
      },
      {
        path: "/addressValidateFeedback",
        element: (
          <ProtectedRoute>
            <AddressValidateFeedback />
          </ProtectedRoute>
        ),
      },
      {
        path: "/geocodeFeedback",
        element: (
          <ProtectedRoute>
            <GeocodeFeedback />
          </ProtectedRoute>
        ),
      },
      {
        path: "/customers/:id",
        element: (
          <ProtectedRoute>
            <CustomerDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: "/customers/:id/api-keys/:apiKeyId/details",
        element: (
          <ProtectedRoute>
            <ApiKeyDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: "/faqs",
        element: <Faq />,
      },
      {
        path: "/pods",
        element: (
          <ProtectedRoute>
            <Pods />
          </ProtectedRoute>
        ),
      },
      {
        path: "/address-management",
        element: (
          <ProtectedRoute>
            <AddressManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: "/address-aliases",
        element: (
          <ProtectedRoute>
            <AddressAliases />
          </ProtectedRoute>
        ),
      },
      {
        path: "/address-management/:traceId",
        element: (
          <ProtectedRoute>
            <AddressDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: "/reports/provider-disagreements",
        element: (
          <ProtectedRoute>
            <ProviderDisagreementsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/debugging",
        element: (
          <ProtectedRoute>
            <Debugging />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: "/address-report/:traceId",
    element: <ReportLayout />,
    children: [
      {
        path: "",
        element: (
          <ProtectedRoute>
            <ReportPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: "/address-report-management/:traceId",
    element: <ReportLayout />,
    children: [
      {
        path: "",
        element: (
          <ProtectedRoute>
            <ReportPageManagement />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);
